import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StudentLeaderboardView({ user }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRank, setMyRank] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, [user.id]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      // Get general leaderboard for all teacher's students
      // Since students can be in multiple classes, we'll show a combined ranking
      // In a future version, we could add class filtering
      
      // For now, we'll fetch the general ranking and highlight the current student
      // This would need to be adapted based on your class structure
      
      // Placeholder: Getting all rankings (you might want to filter by specific teacher or class)
      const response = await axios.get(`${BACKEND_URL}/api/student/${user.id}/ranking`);
      
      // For demo purposes, showing a message. You'd need to implement the full class-based leaderboard
      console.log('Student stats:', response.data);
      
      // TODO: Implement actual class-based leaderboard fetch
      // For now, showing empty state
      setLeaderboard([]);
      
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}º`;
  };

  const getRowColor = (rank, isCurrentUser) => {
    if (isCurrentUser) return 'bg-purple-100 border-purple-400 border-2';
    if (rank === 1) return 'bg-yellow-50 border-yellow-300';
    if (rank === 2) return 'bg-gray-50 border-gray-300';
    if (rank === 3) return 'bg-orange-50 border-orange-300';
    return 'bg-white border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-4xl">🏆</span>
          <div>
            <h2 className="text-2xl font-bold">Clasificación General</h2>
            <p className="text-purple-100">Ranking de todos los estudiantes</p>
          </div>
        </div>
        <p className="text-sm text-purple-100 mt-2">
          Los puntos se suman de todas las actividades completadas (Quizzes y TextHunt)
        </p>
      </div>

      {/* Info Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">Próximamente: Clasificación por Clase</h3>
            <p className="text-sm text-blue-700">
              Estamos trabajando en mostrar las clasificaciones separadas por cada una de tus clases. 
              Por ahora, puedes ver tu ranking general y estadísticas en la sección de estadísticas.
            </p>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-600">Cargando clasificación...</p>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay datos de clasificación disponibles
            </h3>
            <p className="text-gray-500 mb-4">
              Completa algunas actividades para aparecer en el ranking
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Posición
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Estudiante
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Puntos Totales
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Quizzes
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    TextHunt
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actividades
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leaderboard.map((entry) => {
                  const isCurrentUser = entry.student_id === user.id;
                  return (
                    <tr
                      key={entry.student_id}
                      className={`${getRowColor(entry.rank, isCurrentUser)} border-l-4 hover:shadow-md transition`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-2xl font-bold text-gray-800">
                          {getMedalEmoji(entry.rank)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {entry.student_picture && (
                            <img
                              src={entry.student_picture}
                              alt={entry.student_name}
                              className="w-10 h-10 rounded-full mr-3"
                            />
                          )}
                          <div>
                            <div className={`text-sm font-bold ${isCurrentUser ? 'text-purple-900' : 'text-gray-900'}`}>
                              {entry.student_name}
                              {isCurrentUser && <span className="ml-2 text-purple-600">(Tú)</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {entry.total_points}
                        </div>
                        <div className="text-xs text-gray-500">puntos</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.total_quizzes}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.total_texthunts}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-bold text-blue-600">
                          {entry.total_activities}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentLeaderboardView;
